/**
 * driverPersonalityCursor — système de "curseur" comportemental des
 * chauffeurs (Phase F2, 04/02/2026, demande utilisateur).
 *
 * Le doc « Analyse catalogue flotte » liste 14 axes de personnalité
 * qualitatifs. Cette utilitaire les transforme en valeurs quantifiées
 * (-2..+2) calculées à partir des `personalityTraits[]` du chauffeur,
 * pour pouvoir les rendre visuellement (curseurs colorés) dans la
 * fiche détail RH.
 *
 * Les modificateurs sont déclarés sur chaque trait dans
 * `data/personalityTraits.js`. Si un chauffeur cumule deux traits, les
 * modificateurs s'ajoutent puis sont clampés sur [-2, +2].
 *
 * Le résultat est dérivé à la volée (pas de persistance) — donc aucune
 * migration de save n'est nécessaire.
 *
 * 10/02/2026 (Lot J3) — Extension de 9 → 13 axes pour couvrir les 14
 * dimensions du doc analyse flotte. Permis CE est volontairement laissé
 * en dehors des sliders : déjà géré par `driver.licenses[]` (catégorique,
 * pas un curseur continu). Les 4 axes ajoutés : `contractType`,
 * `levelUp`, `fatigueResistance`, `groupRole`.
 */
import { PERSONALITY_TRAITS } from '../data/personalityTraits';

/**
 * Liste des axes du curseur. L'ordre est utilisé par l'UI pour
 * l'affichage en colonnes.
 */
export const PERSONALITY_AXES = [
  { id: 'longDistance',      label: 'Longue distance',          pole: ['Contre', 'Adore'] },
  { id: 'contractType',      label: 'Type de contrat',          pole: ['Ponctuel', 'Régulier'] },
  { id: 'exportUE',          label: 'Export UE',                pole: ['Réfractaire', 'Volontaire'] },
  { id: 'training',          label: 'Formations',               pole: ['Réfractaire', 'Demandeur'] },
  { id: 'comfort',           label: 'Confort camion',           pole: ['Indifférent', 'Exige du luxe'] },
  { id: 'electric',          label: 'Véhicule électrique',      pole: ['Contre', 'Favorable'] },
  { id: 'levelUp',           label: 'Évolution niveau',         pole: ['Stagnation', 'Ambitieux'] },
  { id: 'loyalty',           label: 'Fidélité entreprise',      pole: ['Mercenaire', 'Loyal'] },
  { id: 'salary',            label: 'Exigence salariale',       pole: ['Timide', 'Ambitieux'] },
  { id: 'stress',            label: 'Résistance au stress',     pole: ['Sensible', 'Résistant'] },
  { id: 'fatigueResistance', label: 'Résistance à la fatigue',  pole: ['Fragile', 'Endurant'] },
  { id: 'teamFit',           label: 'Ambiance équipe',          pole: ['Solitaire', 'Sociable'] },
  { id: 'groupRole',         label: 'Position dans le groupe',  pole: ['Retrait', 'Moteur'] },
];

/**
 * Modificateurs par défaut par trait. Les traits non listés sont
 * neutres (cursor à 0 sur tous les axes).
 *
 * 10/02/2026 (Lot J3) — Étendu pour couvrir les 4 nouveaux axes
 * `contractType`, `levelUp`, `fatigueResistance`, `groupRole`.
 */
const TRAIT_MODIFIERS = {
  'old-school':      { electric: -2, training: -1, comfort: -1, contractType: +1, levelUp: -1, groupRole: 0 },
  'tech-savvy':      { electric: +2, training: +1, contractType: -1, levelUp: +1 },
  'ambitieux':       { salary: +2, levelUp: +2, longDistance: +1, exportUE: +1, contractType: -1, groupRole: +1 },
  'casanier':        { exportUE: -2, longDistance: -2, contractType: +2, levelUp: -1, groupRole: -1 },
  'introverti':      { teamFit: -1, groupRole: -2 },
  'aventurier':      { longDistance: +2, exportUE: +2, stress: +1, fatigueResistance: +1, contractType: -2, groupRole: 0 },
  'perfectionniste': { training: +1, comfort: +1, stress: -1, fatigueResistance: -1, contractType: +1 },
  'pragmatique':     { comfort: -1, salary: 0, contractType: +1, fatigueResistance: +1 },
  'rebelle':         { loyalty: -2, training: -1, teamFit: -1, groupRole: -1 },
  'prudent':         { stress: -1, training: +1, comfort: +1, electric: +1, fatigueResistance: 0, contractType: +1 },
  'sceptique':       { training: -1, electric: -1, levelUp: -1, groupRole: 0 },
  'enjoué':          { teamFit: +2, stress: +1, groupRole: +2 },
  'loyal':           { loyalty: +2, contractType: +1 },
  'mercenaire':      { loyalty: -2, salary: +1, levelUp: +1, contractType: -1 },
  'fier':            { salary: +1, groupRole: +1, training: -1 },
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Compute the 9-axis cursor for a driver. Returns an object keyed by
 * axis id ; missing axes default to 0.
 *
 * @param {object} driver — driver from gameStore / DRIVERS
 * @returns {Object.<string, number>} cursor values clamped to [-2, +2]
 */
export function derivePersonalityCursor(driver) {
  const cursor = Object.fromEntries(PERSONALITY_AXES.map((a) => [a.id, 0]));
  if (!driver || !Array.isArray(driver.personalityTraits)) return cursor;
  driver.personalityTraits.forEach((traitId) => {
    const mod = TRAIT_MODIFIERS[traitId];
    if (!mod) return;
    Object.entries(mod).forEach(([axis, delta]) => {
      if (cursor[axis] !== undefined) {
        cursor[axis] = clamp(cursor[axis] + delta, -2, 2);
      }
    });
  });
  return cursor;
}

/**
 * Quick label for a cursor value. Used for accessibility + tooltips.
 */
export function cursorValueLabel(v) {
  if (v <= -2) return 'Très négatif';
  if (v === -1) return 'Négatif';
  if (v === 0) return 'Neutre';
  if (v === 1) return 'Positif';
  return 'Très positif';
}
